'use client';

import {styled} from '@mui/material/styles';
import {useSiteConfig} from '@olympus-frontend/tenant-config';
import {MuiTelInput} from 'mui-tel-input';
import {useEffect, useId, useRef, useState} from 'react';

import {phoneState} from '../../forms/phone';
import {FaChevronDownLightIcon, faIconMaskUrl} from '../icon';
import {autofillOverride, FIELD_HEIGHT, FIELD_PADDING_X} from './field-styles';
import {FieldHelperText} from './FieldHelperText';
import {FieldLabel} from './FieldLabel';

const Root = styled('div')({display: 'flex', flexDirection: 'column', width: '100%'});
const Shell = styled('div')({position: 'relative', width: '100%'});

const DEFAULT_LABEL_INSET = 133;

// mui-tel-input omits `InputProps`/`inputProps` and its only render slot is the flag
// button, so there is no way to mount an icon element after the calling code. The glyph
// still comes from the catalog — it is painted as a mask rather than rendered as an element.
const CHEVRON_MASK = faIconMaskUrl(FaChevronDownLightIcon);

const StyledTel = styled(MuiTelInput, {shouldForwardProp: p => p !== 'oError'})<{oError?: boolean}>(({theme, oError}) => {
  const colors = theme.palette.colors;
  const idle = oError ? colors.border.error.default : colors.border.neutral.subtle;
  const divider = colors.border.neutral.default;
  const focusBorder = colors.border.focus.default;
  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      minHeight: FIELD_HEIGHT,
      borderRadius: theme.radii.full,
      backgroundColor: colors.surface.neutral.default,
      boxShadow: 'none',
      // Must equal the label-overlay offset's first term (see useEffect below).
      paddingInlineStart: FIELD_PADDING_X,
      paddingInlineEnd: 0,
      fontSize: theme.font.size['body-2'],
      fontWeight: theme.font.weight.regular,
      lineHeight: 1.2,
      color: colors.text.neutral.strong,
      '& .MuiOutlinedInput-notchedOutline': {borderColor: idle},
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: oError ? colors.border.error.default : colors.border.neutral.hover,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: oError ? colors.border.error.default : focusBorder,
        borderWidth: 1,
      },
      '&.Mui-focused': {boxShadow: 'none'},
      '&.Mui-disabled': {backgroundColor: colors.surface.neutral.disabled},
      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {borderColor: colors.border.neutral.disabled},
      '& input': {
        ...autofillOverride(theme),
        // MUI's default OutlinedInput input padding (16.5px block) pushes the
        // shell past FIELD_HEIGHT, leaving the phone field taller than every
        // other field in the form.
        paddingBlock: 0,
        height: FIELD_HEIGHT - 2,
        boxSizing: 'border-box',
      },
    },
    '& .MuiInputAdornment-positionStart': {
      alignSelf: 'stretch',
      height: 'auto',
      maxHeight: 'none',
      margin: 0,
      cursor: 'pointer',
    },
    // Strip the IconButton chrome so the flag sits tight (Figma has no button affordance).
    '& .MuiTelInput-IconButton': {
      padding: 0,
      marginInlineEnd: theme.spacing(0.75),
      borderRadius: 2,
      '&:hover': {backgroundColor: 'transparent'},
    },
    '& .MuiTelInput-FlagImg': {
      display: 'block',
      width: 22,
      height: 'auto',
      borderRadius: 2,
    },
    // Dial code `+20` (Figtree Medium 14) + chevron, closed by mui-tel-input's own divider
    // (the calling-code border) restyled to the Figma `Line 1` (full field height, faint colour).
    '& .MuiInputAdornment-positionStart .MuiTypography-root': {
      display: 'inline-flex',
      alignItems: 'center',
      alignSelf: 'stretch',
      gap: theme.spacing(0.75),
      paddingInlineEnd: theme.spacing(2.5),
      // Clear mui-tel-input's physical border-right (the rtl plugin flips it to the wrong side on /ar)
      // then redraw the divider on the logical inline-end so it stays correct in both directions.
      border: 'none',
      borderInlineEnd: `1px solid ${divider}`,
      color: colors.text.neutral.default,
      // The field itself now follows document direction (see `Shell` below), so on /ar the dial
      // code sits inside an RTL context — `unicode-bidi: plaintext` resolves its direction from
      // its own content (digits only → LTR) so `+20` doesn't bidi-reorder to `20+`.
      unicodeBidi: 'plaintext',
      fontSize: theme.font.size['body-3'],
      fontWeight: theme.font.weight.regular,
      lineHeight: 1.2,
      '&::after': {
        content: '""',
        flexShrink: 0,
        // Square, because the glyph's ink sits below the centre of its square viewBox —
        // a short box would crop the chevron rather than letterbox it. At 13px the ink
        // lands at ~10.6x5.7, matching the mark this replaced.
        width: 13,
        height: 13,
        pointerEvents: 'none',
        backgroundColor: 'currentColor',
        WebkitMaskImage: CHEVRON_MASK,
        maskImage: CHEVRON_MASK,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      },
    },
    '& .MuiOutlinedInput-input': {
      paddingInlineStart: FIELD_PADDING_X,
      paddingInlineEnd: FIELD_PADDING_X,
      // The input keeps `dir="ltr"` (see `slotProps.htmlInput` below) so typed digits always read
      // left-to-right — but `text-align: start` would resolve against that forced `dir`, i.e.
      // always physical-left, leaving the digits stranded away from the divider once the field
      // mirrors on /ar (dial code moves to the right, number area to its left). Unlike the
      // divider above, here we *want* stylis-plugin-rtl's physical flip: `left` in the LTR sheet
      // becomes `right` in the RTL sheet, keeping the digits flush against the divider/dial code
      // in both directions while the characters themselves stay LTR-ordered.
      textAlign: 'left',
    },
  };
});

export interface PhoneInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  name?: string;
  disabled?: boolean;
}

export function PhoneInput({label, value, onChange, onBlur, required, error, helperText, name, disabled}: PhoneInputProps) {
  const {phoneCountry} = useSiteConfig();
  const helperId = useId();
  const shellRef = useRef<HTMLDivElement>(null);
  const [labelInset, setLabelInset] = useState(DEFAULT_LABEL_INSET);
  // Show the placeholder label only while there's no national number (dial-code-only counts as empty).
  const showLabel = phoneState(value) === 'empty';

  // mui-tel-input renders the dial code + our chevron as a sibling of the flag IconButton, with
  // `pointer-events: none` — so only the flag itself opens the country menu. Clicks on the rest of
  // the adornment land on the adornment element, and are forwarded to the button from here.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }
    const openCountryMenu = (event: MouseEvent) => {
      if (disabled) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (!target.closest('.MuiInputAdornment-positionStart') || target.closest('.MuiTelInput-IconButton')) {
        return;
      }
      shell.querySelector<HTMLButtonElement>('.MuiTelInput-IconButton')?.click();
    };
    shell.addEventListener('click', openCountryMenu);
    return () => {
      shell.removeEventListener('click', openCountryMenu);
    };
  }, [disabled]);

  // Align the overlay label with the number input: past the flag, dial code, chevron and divider.
  // Re-measures when the value (and thus the dial-code width) changes.
  useEffect(() => {
    if (!showLabel) {
      return;
    }
    const adornment = shellRef.current?.querySelector('.MuiInputAdornment-positionStart');
    if (adornment instanceof HTMLElement) {
      setLabelInset(FIELD_PADDING_X + adornment.offsetWidth + FIELD_PADDING_X);
    }
  }, [showLabel, value]);

  return (
    <Root>
      <Shell ref={shellRef}>
        <StyledTel
          fullWidth
          oError={error}
          value={value}
          name={name}
          defaultCountry={phoneCountry}
          forceCallingCode
          disabled={disabled}
          onChange={v => {
            onChange(v);
          }}
          onBlur={onBlur}
          error={error}
          MenuProps={{
            // The country menu portals to <body>, which is RTL on /ar, so the dial codes ("+20")
            // in each option would otherwise bidi-scramble to "20+". `unicode-bidi: plaintext`
            // makes each code resolve direction from its own content (digits only → LTR) — unlike
            // `direction: ltr`, it is NOT flipped by stylis-plugin-rtl in the RTL sheet.
            sx: {'& .MuiTelInput-Typography-calling-code': {unicodeBidi: 'plaintext'}},
          }}
          slotProps={{
            htmlInput: {
              // Keep the actual typed number LTR even though the field shell now mirrors on /ar.
              dir: 'ltr',
              'aria-label': label,
              'aria-required': required || undefined,
              'aria-invalid': error || undefined,
              'aria-describedby': helperText ? helperId : undefined,
            },
          }}
        />
        {showLabel && <FieldLabel label={label} required={required} insetStart={labelInset} />}
      </Shell>
      <FieldHelperText id={helperId} error={error} message={helperText} />
    </Root>
  );
}

PhoneInput.displayName = 'PhoneInput';
